import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PlaceIcon from '@material-ui/icons/Place';
import { RootState } from '../../../../../redux/root';
import SingleItemWithDownload from '../../Common/SingleItemWithDownload';
import { setBottomDrawerOpened } from '../../../../../redux/slices/generalSlice';
import ConditionsControls from './ConditionsControls';
import { setConditionComments, setConditionName, setSelectedConditionItem } from '../../../../../redux/slices/conditionsSlice';
import { getPermitActivityComments } from '../../../../../api/activities';
import { Loader } from '../../../../../atoms';

const ConditionItems = () => {
  const dispatch = useDispatch();
  const conditions = useSelector((s: RootState) => s.conditions.PermitConditions);
  const permitConditionsLoading = useSelector((s: RootState) => s.conditions.permitConditionsLoading);
  const SelectedCondition = useSelector((s: RootState) => s.conditions.SelectedCondition);

  const items = conditions.map(item => ({
    name: `${item.conditionName} - ${item.activityTitleName}`,
    status: item.status,
    statusName: item.statusName,
    id: item.id,
    attachments: item.attachments,
    title: item.title,
  }));

  const onEdit = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(setSelectedConditionItem(item));
    dispatch(setBottomDrawerOpened(true));

    const params = {
      offset: 0,
      is_active: true,
      is_deleted: false,
      limit: 100000000,
      system_message: false,
    };
    const loadComments = async () => {
      const result = await getPermitActivityComments(params, item.id);
      dispatch(setConditionComments(result.data.results));
      dispatch(setConditionName(item.name));
    };
    loadComments();
  };

  return (
    <Root>
      <HeaderCard>
        <HeaderCol>
          <HeaderLabel>Project / Permit</HeaderLabel>
          {/* @ts-ignore */}
          <HeaderTitle title={SelectedCondition?.namePermitName || ''}>
            {/* @ts-ignore */}
            {SelectedCondition?.namePermitName || '—'}
          </HeaderTitle>
        </HeaderCol>
        <HeaderDivider />
        <HeaderCol>
          <HeaderLabel>Address</HeaderLabel>
          <HeaderAddress title={SelectedCondition?.address || ''}>
            <PlaceIcon style={{ fontSize: 16 }} />
            <span>{SelectedCondition?.address || '—'}</span>
          </HeaderAddress>
        </HeaderCol>
      </HeaderCard>

      <SectionEyebrow>
        <span>Conditions</span>
        <ConditionsCount>{items.length}</ConditionsCount>
      </SectionEyebrow>

      {permitConditionsLoading && items.length === 0 ? (
        <LoadingWrap>
          <Loader size={32} thickness={3.5} />
          <LoadingLabel>Loading conditions…</LoadingLabel>
        </LoadingWrap>
      ) : items.length === 0 ? (
        <Empty>No conditions on this permit.</Empty>
      ) : (
        <Grid>
          {items.map((item, index) => (
            <SingleItemWithDownload onEdit={onEdit} key={index} item={item} />
          ))}
        </Grid>
      )}

      <ConditionsControls />
    </Root>
  );
};

export default ConditionItems;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  padding: 0 2px;
`;

const ConditionsCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingLabel = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
  font-weight: 500;
`;
